import * as React from "react";
import { Grid, TextField, Button, Snackbar, Alert, CircularProgress, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUser, login, verifyLoginOtp, resetLoginState, clearAuthError } from "../../../Redux/Auth/Action";
import { useEffect, useState } from "react";

// Email validation regex
const EMAIL_REGEX = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default function LoginUserForm({ handleNext, onFieldsChange }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { auth } = useSelector((store) => store);

  // Form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");

  // Notify parent of field changes
  useEffect(() => {
    if (onFieldsChange) {
      const isDirty = !!(email.trim() || password.trim() || otp.trim());
      onFieldsChange(isDirty);
    }
  }, [email, password, otp, onFieldsChange]);


  // UI states
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [emailError, setEmailError] = useState("");

  // Cooldown timer
  const [cooldown, setCooldown] = useState(0);

  const handleCloseSnackbar = () => {
    setOpenSnackBar(false);
    dispatch(clearAuthError());
  };

  // Get user if JWT exists
  useEffect(() => {
    if (jwt) {
      dispatch(getUser(jwt));
    }
  }, [jwt, dispatch]);

  // Handle successful login (after OTP verification)
  // Akhil comments - Only redirect admin users to /admin dashboard
  // Regular users are redirected by AuthModal using checkoutState
  useEffect(() => {
    if (auth.user && jwt) {
      // Only redirect admin users explicitly - regular users handled by AuthModal
      if (auth.user.role === "ROLE_ADMIN") {
        setSnackbarMessage("Login successful! Redirecting to admin...");
        setSnackbarSeverity("success");
        setOpenSnackBar(true);
        setTimeout(() => {
          navigate("/admin");
        }, 1000);
      }
      // For regular users, AuthModal.jsx handles the redirect using checkoutState
    }
  }, [auth.user, jwt, navigate]);

  // Handle errors
  useEffect(() => {
    if (auth.error) {
      setSnackbarMessage(auth.error);
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
    }
  }, [auth.error]);

  // Cooldown timer for resend OTP
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Validate email format
  const validateEmail = (emailValue) => {
    if (!emailValue) {
      setEmailError("");
      return true;
    }
    if (!EMAIL_REGEX.test(emailValue)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  // Step 1: Submit email and password - triggers OTP send
  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      setSnackbarMessage("Please enter email and password");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    if (!validateEmail(email)) {
      setSnackbarMessage("Please enter a valid email address");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    const result = await dispatch(login({ email, password }));

    if (result.success) {
      if (result.directLogin) {
        // User already verified OTP before - direct login with JWT
        setSnackbarMessage("Login successful!");
        setSnackbarSeverity("success");
        setOpenSnackBar(true);
        // Fetch user profile - this triggers redirect via useEffect
        dispatch(getUser(result.jwt));
      } else if (result.otpSent) {
        // First-time login - need OTP verification
        setSnackbarMessage("OTP sent to your email!");
        setSnackbarSeverity("success");
        setOpenSnackBar(true);
        setCooldown(auth.loginCooldownSeconds || 120);
      }
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setSnackbarMessage("Please enter a valid 6-digit OTP");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    const result = await dispatch(verifyLoginOtp(auth.loginEmail, otp, auth.loginPassword));

    if (result.success) {
      // OTP verified successfully - JWT is stored
      // getUser will update auth.user, which triggers AuthModal to handle redirect
      dispatch(getUser(localStorage.getItem("jwt")));
    }
  };

  // Go back to login step
  const handleBackToLogin = () => {
    dispatch(resetLoginState());
    setOtp("");
    setCooldown(0);
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (cooldown > 0) return;

    const result = await dispatch(login({ email: auth.loginEmail, password: auth.loginPassword }));
    if (result.success) {
      setSnackbarMessage("OTP resent to your email!");
      setSnackbarSeverity("success");
      setOpenSnackBar(true);
      setCooldown(auth.loginCooldownSeconds || 120);
    }
  };

  // Render OTP Verification Screen (Step 2)
  if (auth.loginOtpSent) {
    return (
      <div className="shadow-lg">
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Verify Your Identity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We've sent a 6-digit OTP to <strong>{auth.loginEmail}</strong>
          </Typography>
        </Box>

        {auth.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {auth.error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              id="otp"
              name="otp"
              label="Enter 6-digit OTP"
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              autoFocus
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              className="bg-[#9155FD] w-full"
              variant="contained"
              size="large"
              sx={{ padding: ".8rem 0" }}
              onClick={handleVerifyOtp}
              disabled={auth.isLoading || otp.length !== 6}
            >
              {auth.isLoading ? <CircularProgress size={24} color="inherit" /> : "Verify OTP"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button size="small" onClick={handleBackToLogin}>
                ← Back to Login
              </Button>
              <Button
                size="small"
                onClick={handleResendOtp}
                disabled={cooldown > 0}
              >
                {cooldown > 0 ? `Resend OTP (${cooldown}s)` : "Resend OTP"}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Snackbar
          open={openSnackBar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }

  // Render Login Form (Step 1)
  return (
    <div className="shadow-lg">
      {auth.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {auth.error}
        </Alert>
      )}
      <form className="w-full" onSubmit={handleLoginSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              id="email"
              name="email"
              label="Email"
              fullWidth
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={(e) => validateEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="password"
              name="password"
              label="Password"
              fullWidth
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              className="bg-[#9155FD] w-full"
              type="submit"
              variant="contained"
              size="large"
              sx={{ padding: ".8rem 0" }}
              disabled={auth.isLoading}
            >
              {auth.isLoading ? <CircularProgress size={24} color="inherit" /> : "Login"}
            </Button>
          </Grid>
        </Grid>
      </form>
      <div className="flex justify-center flex-col items-center">
        <div className="py-3 flex items-center">
          <p className="m-0 p-0">Don't have an account?</p>
          <Button onClick={() => navigate("/register")} className="ml-5" size="small">
            Register
          </Button>
        </div>
      </div>
      <Snackbar
        open={openSnackBar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}